import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Custom hook for polling an API endpoint at regular intervals
 * 
 * @param {Function} pollingFunction - The async function to call for polling
 * @param {number} interval - Polling interval in milliseconds (default: 10000)
 * @param {Function} shouldStopPolling - Function that receives the result and returns true to stop polling
 * @returns {Object} - { data, error, isPolling, startPolling, stopPolling }
 */
export const usePolling = (pollingFunction, interval = 10000, shouldStopPolling = null) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [isPolling, setIsPolling] = useState(false);
  
  const intervalRef = useRef(null);
  const isMountedRef = useRef(true);
  const pollingFunctionRef = useRef(pollingFunction);
  const shouldStopPollingRef = useRef(shouldStopPolling);

  // Update refs when they change
  useEffect(() => {
    pollingFunctionRef.current = pollingFunction;
  }, [pollingFunction]);

  useEffect(() => {
    shouldStopPollingRef.current = shouldStopPolling;
  }, [shouldStopPolling]);

  // Use ref untuk stopPolling untuk avoid dependency issues
  const stopPollingRef = useRef(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setIsPolling(false);
  });

  const stopPolling = useCallback(() => {
    stopPollingRef.current();
  }, []);

  // Inline poll function to avoid dependency issues
  // This prevents startPolling from recreating when poll reference changes
  const startPolling = useCallback(() => {
    // Stop any existing polling first to prevent multiple instances
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    
    setIsPolling(true);
    setError(null);
    
    // Define poll function inline to avoid dependency on external poll callback
    const pollFn = async () => {
      try {
        const result = await pollingFunctionRef.current();
        
        if (!isMountedRef.current) return;
        
        setData(result);
        setError(null);

        // Check if we should stop polling
        if (shouldStopPollingRef.current && shouldStopPollingRef.current(result)) {
          stopPollingRef.current();
        }
      } catch (err) {
        if (!isMountedRef.current) return;
        
        setError(err);
        // Only log errors in development
        if (process.env.NODE_ENV === 'development') {
          console.error('Polling error:', err);
        }
        // Don't stop polling on error - let it retry on next interval
        // Only stop if it's a critical error (400/404)
        if (err.response?.status === 404 || err.response?.status === 400) {
          stopPollingRef.current();
        }
        // Otherwise, continue polling on next interval
      }
    };
    
    // Poll immediately
    pollFn();
    
    // Then poll at interval
    intervalRef.current = setInterval(() => {
      if (isMountedRef.current) {
        pollFn();
      } else {
        stopPollingRef.current();
      }
    }, interval);
  }, [interval]); // Only interval as dependency - poll function is inline

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    
    return () => {
      isMountedRef.current = false;
      stopPollingRef.current(); // Use ref instead of function
    };
  }, []); // Empty dependency - only cleanup on unmount

  // changed by Cursor: allow parent to clear stale data before retrying (prevents usePollingResult from re-firing on old error)
  const resetData = useCallback(() => {
    setData(null);
    setError(null);
  }, []);

  return {
    data,
    error,
    isPolling,
    startPolling,
    stopPolling,
    resetData,
  };
};

export default usePolling;

